import { useState, useEffect, useRef } from 'react';
import heroSecurity from '../../../assets/images/heroSecurity.jpg';

export default function Hero() {
  const [counted, setCounted] = useState(false);
  const [count50, setCount50] = useState(0);
  const [count999, setCount999] = useState(0);
  const statsRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (counted) return; // Đã chạy rồi thì không chạy nữa

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !counted) {
            setCounted(true);
            
            // Count up animation for 50+
            const duration = 2000;
            const steps = 50;
            const increment = 50 / steps;
            let current = 0;
            const timer = setInterval(() => {
              current += increment;
              if (current >= 50) {
                setCount50(50);
                clearInterval(timer);
              } else {
                setCount50(Math.floor(current));
              }
            }, duration / steps);

            // Count up animation for 99.9%
            let current999 = 0;
            const increment999 = 99.9 / steps;
            const timer999 = setInterval(() => {
              current999 += increment999;
              if (current999 >= 99.9) {
                setCount999(99.9);
                clearInterval(timer999);
              } else {
                setCount999(Number(current999.toFixed(1)));
              }
            }, duration / steps);

            // Disconnect observer sau khi đã chạy
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [counted]);

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center pt-32 pb-16 overflow-hidden">
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a2942] via-[#2a4b7c] to-[#1e3a5f]"></div>
      
      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite'
        }}></div>
      </div>

      {/* Floating orbs */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-[#06b6d4]/20 rounded-full blur-3xl animate-[float_8s_ease-in-out_infinite]"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#06b6d4]/10 rounded-full blur-3xl animate-[float_10s_ease-in-out_infinite]" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/3 w-56 h-56 bg-[#0891b2]/10 rounded-full blur-3xl animate-[float_12s_ease-in-out_infinite]" style={{ animationDelay: '4s' }}></div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-md animate-[fadeInDown_0.6s_ease-out]">
              <div className="relative">
                <div className="w-2 h-2 rounded-full bg-[#06b6d4]"></div>
                <div className="absolute inset-0 w-2 h-2 rounded-full bg-[#06b6d4] animate-ping"></div>
              </div>
              <span className="text-white/90 text-sm font-medium tracking-wide">Security First Approach</span>
            </div>

            {/* Main Heading */}
            <div className="space-y-4 animate-[fadeInUp_0.8s_ease-out_0.2s_both]">
              <h1 className="font-['Inter',sans-serif] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1]">
                Security Assessment
                <span className="block mt-2 bg-gradient-to-r from-[#06b6d4] to-[#0891b2] bg-clip-text text-transparent">
                  & Code Review
                </span>
              </h1>
            </div>

            {/* Subheading */}
            <p className="text-lg md:text-xl text-white/90 font-light leading-relaxed animate-[fadeInUp_0.8s_ease-out_0.3s_both]">
              <span className="text-[#06b6d4] font-semibold">Phát hiện sớm lỗ hổng</span> – Giảm thiểu rủi ro – Bảo vệ hệ thống
            </p>

            {/* Description */}
            <p className="text-sm md:text-base text-white/70 leading-relaxed max-w-xl animate-[fadeInUp_0.8s_ease-out_0.4s_both]">
              Stackway cung cấp dịch vụ <strong className="text-white/90 font-semibold">đánh giá bảo mật và rà soát mã nguồn</strong>, giúp doanh nghiệp chủ động phát hiện và khắc phục các rủi ro an toàn thông tin trước khi xảy ra sự cố.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4 pt-4 animate-[fadeInUp_0.8s_ease-out_0.5s_both]">
              <button 
                onClick={() => scrollToSection('use-cases')}
                className="group relative bg-[#06b6d4] text-white px-7 py-3.5 rounded-full text-base font-semibold hover:bg-[#0891b2] transition-all duration-300 flex items-center gap-2 cursor-pointer whitespace-nowrap overflow-hidden"
              >
                <span className="relative z-10">Khám phá ngay</span>
                <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform relative z-10"></i>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0891b2] to-[#06b6d4] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              <button 
                onClick={() => scrollToSection('getting-started')}
                className="group bg-transparent border-2 border-white/30 text-white px-7 py-3.5 rounded-full text-base font-semibold hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                Tư vấn miễn phí
              </button>
            </div>

            {/* Trust indicators */}
            <div ref={statsRef} className="grid grid-cols-3 gap-8 pt-10 border-t border-white/10 animate-[fadeInUp_0.8s_ease-out_0.6s_both]">
              <div className="group cursor-default">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#06b6d4] to-[#0891b2] bg-clip-text text-transparent mb-1 group-hover:scale-110 transition-transform duration-300">
                  {count50}+
                </div>
                <div className="text-xs text-white/60 uppercase tracking-wider">Doanh nghiệp</div>
              </div>
              <div className="group cursor-default">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#06b6d4] to-[#0891b2] bg-clip-text text-transparent mb-1 group-hover:scale-110 transition-transform duration-300">
                  {count999}%
                </div>
                <div className="text-xs text-white/60 uppercase tracking-wider">Phát hiện</div>
              </div>
              <div className="group cursor-default">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#06b6d4] to-[#0891b2] bg-clip-text text-transparent mb-1 group-hover:scale-110 transition-transform duration-300">24/7</div>
                <div className="text-xs text-white/60 uppercase tracking-wider">Hỗ trợ</div>
              </div>
            </div>
          </div>

          {/* Right: Visual */}
          <div className="relative hidden lg:block animate-[fadeInRight_1s_ease-out_0.4s_both]">
            <div className="relative">
              {/* Main card with glass effect */}
              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl p-6 shadow-2xl hover:scale-[1.02] transition-transform duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                <img 
                  src={heroSecurity}
                  alt="Security Assessment Visualization"
                  className="w-full h-auto object-cover rounded-xl relative z-10"
                />
                
                {/* Floating badges */}
                <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-3 py-2 animate-[float_3s_ease-in-out_infinite] z-20">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#06b6d4] to-[#0891b2] flex items-center justify-center">
                      <i className="ri-shield-check-line text-white text-base"></i>
                    </div>
                    <div>
                      <div className="text-white text-xs font-semibold">Protected</div>
                      <div className="text-white/60 text-[10px]">100% Secure</div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-3 py-2 animate-[float_3s_ease-in-out_infinite] z-20" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#0891b2] to-[#06b6d4] flex items-center justify-center">
                      <i className="ri-bug-line text-white text-base"></i>
                    </div>
                    <div>
                      <div className="text-white text-xs font-semibold">Vulnerabilities</div>
                      <div className="text-[#06b6d4] text-[10px] font-bold">0 Found</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#06b6d4]/20 rounded-full blur-3xl animate-[pulse_4s_ease-in-out_infinite]"></div>
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#0891b2]/15 rounded-full blur-3xl animate-[pulse_4s_ease-in-out_infinite]" style={{ animationDelay: '2s' }}></div>
              
              {/* Floating particles */}
              <div className="absolute top-1/4 -left-3 w-2.5 h-2.5 bg-[#06b6d4] rounded-full animate-[float_5s_ease-in-out_infinite]"></div>
              <div className="absolute top-1/2 -right-3 w-2 h-2 bg-[#0891b2] rounded-full animate-[float_6s_ease-in-out_infinite]" style={{ animationDelay: '1s' }}></div>
              <div className="absolute bottom-1/4 -left-4 w-2 h-2 bg-[#06b6d4] rounded-full animate-[float_7s_ease-in-out_infinite]" style={{ animationDelay: '2s' }}></div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes gridMove {
          0% { transform: translateY(0); }
          100% { transform: translateY(50px); }
        }
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </section>
  );
}